import { Formik, Form as FormikForm, useField } from "formik";
import useLocalStorage from "helpers/useLocalStorage";
import React, { useEffect, useState } from "react";
import { Alert } from "components/ui/BootstrapCompat";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCommentDots,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaTimes,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AFFILIATE_CODE_STORAGE_KEY,
  BUSINESS_ID_STORAGE_KEY,
  getTokenExpirationTime,
  TOKEN_STORAGE_KEY,
} from "helpers/authSession";
import {
  DonateLoginPayload,
  DonateRegisterPayload,
  publicRegister,
  publicSignIn,
} from "services/main/public.service";
import { useTheme } from "hooks/useTheme";
import { FiMoon, FiSun } from "react-icons/fi";

type AuthMode = "login" | "register";
type RegisterStep = 1 | 2 | 3;

type AuthFormValues = {
  identifier: string;
  password: string;
  name: string;
  email: string;
  username: string;
  phone: string;
  referrer: string;
  confirmPassword: string;
};

type AuthInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  name: keyof AuthFormValues;
};

const formInitialValues: AuthFormValues = {
  identifier: "",
  password: "",
  name: "",
  email: "",
  username: "",
  phone: "",
  referrer: "",
  confirmPassword: "",
};

const getJwtFromResponse = (response: any) =>
  response?.jwt ||
  response?.token ||
  response?.accessToken ||
  response?.data?.jwt ||
  response?.data?.token ||
  response?.data?.accessToken ||
  "";

const getBusinessIdFromResponse = (response: any) =>
  response?.data?.businessId || response?.businessId || "";

const getAffiliateCodeFromResponse = (response: any) => {
  const payloads = [
    response,
    response?.data,
    response?.data?.user,
    response?.user,
    response?.profile,
    response?.data?.profile,
  ];

  for (const payload of payloads) {
    const code =
      payload?.affiliateCode ||
      payload?.affiliate_code ||
      payload?.referralCode ||
      payload?.referral_code ||
      payload?.refCode ||
      payload?.ref_code ||
      payload?.inviteCode ||
      payload?.invite_code;

    if (code) return String(code).trim();
  }

  return "";
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const normalizeUsername = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const isValidUsername = (value: string) => /^[a-z0-9]+$/.test(value);

const getInitialAuthFormValues = (search: string): AuthFormValues => {
  const queryParams = new URLSearchParams(search);
  const referrer =
    queryParams.get("referrer") ||
    queryParams.get("referral") ||
    queryParams.get("ref") ||
    queryParams.get("invite") ||
    "";

  return {
    ...formInitialValues,
    referrer: referrer.trim(),
  };
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const initialMode: AuthMode = location.pathname.includes("register")
    ? "register"
    : "login";
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [registerStep, setRegisterStep] = useState<RegisterStep>(1);
  const [token, setToken] = useLocalStorage(TOKEN_STORAGE_KEY, "");
  const [, setBusinessId] = useLocalStorage(BUSINESS_ID_STORAGE_KEY, "");
  const [, setAffiliateCode] = useLocalStorage(AFFILIATE_CODE_STORAGE_KEY, "");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isMobileShowcaseOpen, setIsMobileShowcaseOpen] = useState(true);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    setMode(initialMode);

    if (initialMode === "register") {
      setRegisterStep(1);
    }
  }, [initialMode]);

  useEffect(() => {
    if (!token) return;

    const expirationTime = getTokenExpirationTime(token);

    if (!expirationTime || Date.now() >= expirationTime) {
      setToken("");
      setBusinessId("");
      setAffiliateCode("");
      return;
    }

    navigate("/dashboard", { replace: true });
  }, [token, navigate, setToken, setBusinessId, setAffiliateCode]);

  const saveSession = (response: any) => {
    const jwt = getJwtFromResponse(response);

    if (jwt) {
      setToken(jwt);
      setBusinessId(getBusinessIdFromResponse(response));
      setAffiliateCode(getAffiliateCodeFromResponse(response));
    }

    return jwt;
  };

  const onLogin = async (values: DonateLoginPayload) => {
    const response = await publicSignIn(values);
    saveSession(response);
  };

  const onRegister = async (values: DonateRegisterPayload) => {
    const response = await publicRegister(values);
    const jwt = saveSession(response);

    if (!jwt) {
      await onLogin({
        identifier: values.username,
        password: values.password,
      });
    }
  };

  const validateForm = (values: AuthFormValues) => {
    const errors: Partial<Record<keyof AuthFormValues, string>> = {};

    if (mode === "login") {
      if (!values.identifier.trim()) {
        errors.identifier = "ກະລຸນາປ້ອນ username ຫຼື email";
      }

      if (!values.password) {
        errors.password = "ກະລຸນາປ້ອນລະຫັດຜ່ານ";
      }

      return errors;
    }

    const username = values.username.trim();

    if (!username) {
      errors.username = "ກະລຸນາຕັ້ງຊື່ລິ້ງຂອງທ່ານ";
    } else if (!isValidUsername(username)) {
      errors.username = "Username ໃຊ້ໄດ້ສະເພາະ a-z ແລະ 0-9";
    }

    if (registerStep === 2) {
      if (!values.email.trim()) {
        errors.email = "ກະລຸນາປ້ອນອີເມວ";
      } else if (!isValidEmail(values.email)) {
        errors.email = "ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ";
      }

      if (!values.password) {
        errors.password = "ກະລຸນາປ້ອນລະຫັດຜ່ານ";
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = "ກະລຸນາຢືນຢັນລະຫັດຜ່ານ";
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = "ລະຫັດຜ່ານບໍ່ກົງກັນ";
      }
    }

    if (registerStep === 3) {
      if (!values.name.trim()) {
        errors.name = "ກະລຸນາປ້ອນຊື່ຂອງທ່ານ";
      }

      if (!values.phone.trim()) {
        errors.phone = "ກະລຸນາປ້ອນເບີໂທລະສັບ";
      }

      if (values.referrer.trim().length > 80) {
        errors.referrer = "ຂໍ້ມູນຜູ້ແນະນຳຍາວເກີນໄປ";
      }
    }

    return errors;
  };

  const onSubmit = async (values: AuthFormValues) => {
    setErrorMsg("");
    setSuccessMsg("");

    try {
      if (mode === "login") {
        await onLogin({
          identifier: values.identifier.trim(),
          password: values.password,
        });
        return;
      }

      if (registerStep < 3) {
        setRegisterStep((currentStep) => (currentStep + 1) as RegisterStep);
        return;
      }

      const referrer = values.referrer.trim();

      await onRegister({
        name: values.name.trim(),
        email: values.email.trim(),
        username: normalizeUsername(values.username),
        phone: values.phone.trim(),
        password: values.password,
        confirmPassword: values.confirmPassword,
        ...(referrer ? { referrer } : {}),
      });
      setSuccessMsg("ສ້າງບັນຊີສຳເລັດ");
    } catch (error: any) {
      const fallbackMessage =
        mode === "login"
          ? "ຊື່ຜູ້ໃຊ້ ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ"
          : "ສ້າງບັນຊີບໍ່ສຳເລັດ ກະລຸນາກວດຂໍ້ມູນອີກຄັ້ງ";
      setErrorMsg(error?.response?.data?.message || fallbackMessage);
    }
  };

  const switchMode = (nextMode: AuthMode) => {
    setErrorMsg("");
    setSuccessMsg("");
    setMode(nextMode);
    setRegisterStep(1);
    navigate(nextMode === "register" ? "/register" : "/login", {
      replace: true,
    });
  };

  const handleBack = () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (mode === "register" && registerStep > 1) {
      setRegisterStep((currentStep) => (currentStep - 1) as RegisterStep);
      return;
    }

    switchMode(mode === "register" ? "login" : "register");
  };

  const isLogin = mode === "login";
  const registerCopy = getRegisterCopy(registerStep);
  const submitText = isLogin
    ? "ເຂົ້າສູ່ລະບົບ"
    : registerStep === 3
      ? "ສ້າງບັນຊີ"
      : "ດຳເນີນການຕໍ່";

  return (
    <div className="auth-page">
      <AuthShowcase
        isMobileOpen={isMobileShowcaseOpen}
        onMobileClose={() => setIsMobileShowcaseOpen(false)}
      />

      <main className="auth-panel">
        <button
          type="button"
          className="auth-theme-toggle"
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Light mode" : "Dark mode"}
          onClick={toggleTheme}
        >
          {isDark ? <FiSun /> : <FiMoon />}
          <span>{isDark ? "Light" : "Dark"}</span>
        </button>

        <button type="button" className="auth-back-button" onClick={handleBack}>
          <FaArrowLeft />
          <span>{isLogin || registerStep > 1 ? "ກັບຄືນ" : "ກັບສູ່ບັນຊີ"}</span>
        </button>

        <div className="auth-form-wrap">
          <img
            className="auth-mobile-logo"
            src="/images/logo.png"
            alt="OneTop"
          />

          <header className="auth-heading">
            <h1>{isLogin ? "ເຂົ້າສູ່ລະບົບ" : registerCopy.title}</h1>
            <p>
              {isLogin
                ? "ກອກຂໍ້ມູນເພື່ອເຂົ້າ dashboard ຂອງທ່ານ"
                : registerCopy.description}
            </p>
          </header>

          <Formik
            initialValues={getInitialAuthFormValues(location.search)}
            validate={validateForm}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <FormikForm className="auth-register-form">
                {isLogin ? (
                  <LoginFields
                    onForgotPassword={() => navigate("/forgot-password")}
                  />
                ) : (
                  <RegisterFields
                    step={registerStep}
                    onStreamlabs={() => {
                      setSuccessMsg("");
                      setErrorMsg("Streamlabs login ຍັງບໍ່ເປີດໃຊ້ງານ");
                    }}
                  />
                )}

                {errorMsg && (
                  <Alert
                    variant="danger"
                    className="auth-alert mb-0 mt-4 border-0"
                  >
                    {errorMsg}
                  </Alert>
                )}
                {successMsg && (
                  <Alert
                    variant="success"
                    className="auth-alert mb-0 mt-4 border-0"
                  >
                    {successMsg}
                  </Alert>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="auth-submit-button"
                >
                  <span>{isSubmitting ? "ກຳລັງດຳເນີນການ..." : submitText}</span>
                  {!isSubmitting && <FaArrowRight />}
                </button>
              </FormikForm>
            )}
          </Formik>

          <p className="auth-switch">
            {isLogin ? "ຍັງບໍ່ມີບັນຊີ?" : "ມີບັນຊີແລ້ວ"}{" "}
            <button
              type="button"
              onClick={() => switchMode(isLogin ? "register" : "login")}
            >
              {isLogin ? "ສ້າງບັນຊີ" : "ເຂົ້າສູ່ລະບົບ"}
            </button>
          </p>
        </div>

        <AuthFooter />
      </main>
    </div>
  );
}

function AuthShowcase({
  isMobileOpen,
  onMobileClose,
}: {
  isMobileOpen: boolean;
  onMobileClose: () => void;
}) {
  return (
    <div
      className={`auth-poster-mobile-layer ${
        isMobileOpen ? "" : "auth-poster-mobile-dismissed"
      }`}
    >
      <aside
        aria-label="OneTop donate preview"
        className="auth-poster-showcase bg-primary-500 dark:bg-primary-900"
      >
        <button
          type="button"
          className="auth-poster-close"
          aria-label="Close promotion"
          title="Close"
          onClick={onMobileClose}
        >
          <FaTimes />
        </button>
        <span className="auth-poster-glow auth-poster-glow-top" />
        <span className="auth-poster-glow auth-poster-glow-bottom" />
        <div className="auth-poster-frame h-full w-full flex items-center justify-center p-[20px]">
          <img
            src="/images/poster.png"
            alt="OneTop"
            className="auth-poster-image w-full rounded-lg"
          />
        </div>
      </aside>
    </div>
  );
}

function LoginFields({ onForgotPassword }: { onForgotPassword: () => void }) {
  return (
    <div className="auth-field-stack">
      <AuthInput
        label="ຊື່ຜູ້ໃຊ້ ຫຼື Email"
        name="identifier"
        type="text"
        placeholder="example@gmail.com"
        autoComplete="username"
      />
      <PasswordInput
        label="ລະຫັດຜ່ານ"
        name="password"
        placeholder="ປ້ອນລະຫັດຜ່ານ"
        autoComplete="current-password"
      />
      <div className="auth-forgot-row">
        <button type="button" onClick={onForgotPassword}>
          ລືມລະຫັດຜ່ານ?
        </button>
      </div>
    </div>
  );
}

function RegisterFields({
  step,
  onStreamlabs,
}: {
  step: RegisterStep;
  onStreamlabs: () => void;
}) {
  if (step === 1) {
    return (
      <div className="auth-field-stack">
        <UsernameInput />
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="auth-field-stack">
        <button
          type="button"
          className="auth-streamlabs-button"
          onClick={onStreamlabs}
        >
          <FaCommentDots />
          <span>ເຂົ້າດ້ວຍ Streamlabs</span>
        </button>
        <div className="auth-divider">
          <span>ຫຼື</span>
        </div>
        <AuthInput
          label="ອີເມວ"
          name="email"
          type="email"
          placeholder="example@gmail.com"
          autoComplete="email"
        />
        <PasswordInput
          label="ລະຫັດຜ່ານ"
          name="password"
          placeholder="ປ້ອນລະຫັດຜ່ານ"
          autoComplete="new-password"
        />
        <PasswordInput
          label="ຢືນຢັນລະຫັດຜ່ານ"
          name="confirmPassword"
          placeholder="ຢືນຢັນລະຫັດຜ່ານ"
          autoComplete="new-password"
        />
      </div>
    );
  }

  return (
    <div className="auth-field-stack">
      <AuthInput
        label="ຊື່ຮ້ານ"
        name="name"
        type="text"
        placeholder="ປ້ອນຊື່ຂອງທ່ານ"
        autoComplete="name"
      />
      <AuthInput
        label="ເບີໂທລະສັບ"
        name="phone"
        type="tel"
        placeholder="20 XXXX XXXX"
        autoComplete="tel"
      />
      <AuthInput
        label="ຜູ້ແນະນຳ (ຖ້າມີ)"
        name="referrer"
        type="text"
        placeholder="username ຫຼື referral code"
        autoComplete="off"
        maxLength={80}
      />
    </div>
  );
}

function AuthInput({ label, name, className = "", ...props }: AuthInputProps) {
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);

  return (
    <label className="auth-field" htmlFor={name}>
      {label && <span>{label}</span>}
      <input
        {...field}
        {...props}
        id={name}
        name={name}
        aria-invalid={showError}
        className={`auth-control ${showError ? "is-invalid" : ""} ${className}`.trim()}
      />
      {showError && <small>{meta.error}</small>}
    </label>
  );
}

function UsernameInput() {
  const [field, meta, helpers] = useField("username");
  const showError = Boolean(meta.touched && meta.error);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(normalizeUsername(event.target.value));
  };

  return (
    <label className="auth-field" htmlFor="username">
      <div
        className={`auth-username-control ${
          showError ? "is-invalid" : ""
        }`.trim()}
      >
        <span>donate.onetop.la/</span>
        <input
          {...field}
          id="username"
          name="username"
          type="text"
          placeholder="yourname"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          aria-invalid={showError}
          onChange={handleChange}
        />
      </div>
      {showError && <small>{meta.error}</small>}
    </label>
  );
}

function PasswordInput({
  label,
  name,
  placeholder,
  autoComplete,
}: AuthInputProps) {
  const [visible, setVisible] = useState(false);
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);

  return (
    <label className="auth-field" htmlFor={name}>
      {label && <span>{label}</span>}
      <div
        className={`auth-password-control ${
          showError ? "is-invalid" : ""
        }`.trim()}
      >
        <input
          {...field}
          id={name}
          name={name}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={showError}
        />
        <button
          type="button"
          onClick={() => setVisible((currentValue) => !currentValue)}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
      {showError && <small>{meta.error}</small>}
    </label>
  );
}

function AuthFooter() {
  return (
    <footer className="auth-footer">
      <span>© 2026 OneTop Labs, Inc.</span>
      <nav>
        <button type="button">
          <FaLock />
          <span>ເງື່ອນໄຂການໃຊ້</span>
        </button>
        <a href="/policy">ນະໂຍບາຍຄວາມເປັນສ່ວນຕົວ</a>
        <button type="button" className="auth-contact-button">
          ຕິດຕໍ່ສອບຖາມ
        </button>
      </nav>
    </footer>
  );
}

const getRegisterCopy = (step: RegisterStep) => {
  if (step === 1) {
    return {
      title: "ສ້າງບັນຊີ",
      description: "ຕັ້ງຊື່ລິ້ງຮັບເງິນໂດເນດຂອງທ່ານ",
    };
  }

  if (step === 2) {
    return {
      title: "ຢືນຢັນຕົວຕົນ",
      description: "ຕັ້ງອີເມວ ແລະລະຫັດຜ່ານຂອງທ່ານ",
    };
  }

  return {
    title: "ຂໍ້ມູນຂອງທ່ານ",
    description: "ຂໍ້ມູນໃຊ້ສຳລັບບັນຊີຮ້ານຂອງທ່ານ",
  };
};
